/**
 * 茨城県の料理12品。レベルが上がるほど大きく、器つきの汁物・鍋物になる。
 *
 * 選定のときに迷った点を残しておく。
 * - 「こも豆腐（つと豆腐）」は岐阜にすでに同じ名前の品があるので外した。
 * - 「わら納豆」は新潟の「きりざい」と食材が重なるが、きりざいは納豆に刻んだ野菜を混ぜたもの、
 *   わら納豆は藁苞に一つずつ煮豆を詰めて発酵させたものなので、形も中身も別物として入れた。
 * - 「干しいも」は島根の干し柿や熊本のいきなり団子と違い、厚さ1センチの細長い板である。
 * - 「鯉の唐揚げ」は福島の鯉の甘露煮と重なるので外した。
 * - 「あんこうの共酢」も魅力的だが、あんこうは鍋のほうを採って一品に絞った。
 * - 「小倉れんこん」の紫峰色の紫峰は筑波山の別称。景色の筑波山とつながる。
 *
 * 2026-10-02 確定案（audit/_確定案.csv）で料理を入れ替えた。
 * - 外した品：ピーナッツ味噌・紫錦梅・小倉れんこん・たがね餅・凍みこんにゃく・パイタ焼き・うなぎの帆引き煮・いわしの卯の花漬け（店で食べられる品に差し替え）
 * - 干し芋の天ぷら：干し芋を天ぷらにして食べる習わしは、公式資料で確かめられなかったので書かない
 * - 干し芋の天ぷら：【暫定】公式の出典が見つかっていない。説明は絵の内容に沿った一般的な記述だけにした。出典が見つかるまで仮の文（2026-10-02）
 * - 那珂湊焼きそば：具（豚肉・キャベツ・玉ねぎ）や青のり・紅しょうがは、公式資料に書かれていないので書かない
 * - 那珂湊焼きそば：B-1グランプリ・愛Bリーグへの加盟は料理の説明ではないので書かない
 * - けんちんそば（つけけんちん）：headline の「冷たいそば」は、出典の「ざるそばを別にそえて」からの言い換え
 * - 「五目いなりずし」を「笠間いなり寿司」に改め、絵と説明を描き直した（同じ料理なので ID は gomoku-inari のまま）。
 * - 笠間いなり寿司：description の「油揚げの中に」は、市の「そばや胡桃が中に入っています」と観光いばらきの「油揚げといえばいなり寿司」から書いた
 * - しらす丼：漁獲量の全国順位（ページでは平成30年調べで全国4位、観光いばらきでは「全国トップクラス」）は年で変わるので書かない
 * - しらす丼：生しらすがとれる季節は公式資料で確かめられなかったので書かない
 * - 奥久慈しゃも親子丼：茨城県のページにある「日本三大地鶏の一つとも言われ」は書いてもよいが、比内地鶏（秋田）でも別の「三大」が出るため、どちらも入れるかは全体で決めてほしいので今回は書かない
 * - 奥久慈しゃも親子丼：飼育日数は大子町観光協会（雄120日・雌150日）と茨城県（雄125日・雌155日）で違う。tip は県の数字を使い、tipSource を県のページにした
 * - 霞ヶ浦のわかさぎの天ぷら：漁獲量の順位（県のページでは「全国トップクラス」）は年で変わるので書かない
 * - 牛久沼のうな重：「うな丼発祥の地」は市の表現なので、「市は〜としている」と主語を付けて書いた
 * - 牛久沼のうな重：うな丼が広まった経路（茶屋が出して水戸街道の名物になった説、今助の芝居小屋から江戸に広まった説、重箱でうな重になった説）は市も「いくつかの説」としているので書かない
 * - 牛久沼のうな重：店名（市のページに桑名屋・伊勢屋・鶴舞家・山水閣とある）は書かない
 * - 常陸牛ステーキ：海外への輸出の話は料理の説明ではないので書かない
  * - 2026-10-12 地元民レビューで「干し芋の天ぷら」を外し「スタミナラーメン」を入れた（地元の人がまず挙げる品のため。旧IDは idMigrations の retiredItems）
 */

import { attachHulls } from '../../utils/itemShape';
import type { RewardLink, StageDefinition, StageItemDefinition } from '../types';
import { ibarakiSceneryTour } from './ibarakiScenes';
import { IBARAKI_FOOD_HULLS } from './ibarakiFoodShapes.generated';

function searchLinks(area: string, dish: string): RewardLink[] {
  return [
    {
      label: '地図で探す',
      url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${area} ${dish}`)}`,
    },
    {
      label: 'お店を調べる',
      url: `https://www.google.com/search?q=${encodeURIComponent(`${area} ${dish}`)}`,
    },
  ];
}

const FOOD_ITEMS: StageItemDefinition[] = [
  {
    id: 'hoshiimo',
    level: 1,
    name: '干しいも',
    shortName: '干しいも',
    image: '/assets/foods/ibaraki/01_hoshiimo.webp',
    radius: 17,
    mass: 1,
    score: 2,
    description: 'さつまいもを蒸してから皮をむき、縦1センチ幅に切って、風通しのよい場所で約1週間天日に干す。ピアノ線や釣り糸で切るときれいに仕上がる。出荷は11月から3月中旬が一般的。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/hoshiimo_ibaraki.html',
    icon: { label: '干しいも', shape: 'mochi', baseColor: '#d9a441', accentColor: '#f2e4c0' },
    reward: {
      unlockScore: 200,
      area: 'ひたちなか市／東海村／那珂市',
      headline: '全国トップのシェア',
      tip: '発祥は静岡県とされ、栗林庄蔵が「煮切り干し法」を開発したことが始まり。茨城へは照沼勘太郎が静岡で習得して伝えた。かつては冬の風物詩として軒先に干される光景が見られた。',
      links: searchLinks('ひたちなか市', '干しいも'),
    },
  },
  {
    id: 'wara-natto',
    level: 2,
    name: 'わら納豆',
    shortName: 'わら納豆',
    image: '/assets/foods/ibaraki/02_wara_natto.webp',
    radius: 21,
    mass: 1.3,
    score: 4,
    description: '束ねた稲わらの中に、一つずつ手作業で煮豆を詰めて発酵させる。水戸の納豆は小粒大豆を使うのが特徴で、那珂川の流域では水害に強いことから多く作られた。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/traditional-foods/menu/natto_ibaraki.html',
    icon: { label: 'わら納豆', shape: 'dumpling', baseColor: '#d9c088', accentColor: '#c2a04a' },
    reward: {
      unlockScore: 500,
      area: '水戸市',
      headline: 'わらに一粒ずつ詰める',
      tip: '1083年、源義家が渡里町の館に滞在した際、わらに包んだ煮豆が自然に発酵したという伝承がある。1889年の鉄道開通後、水戸駅で売られて全国に知られた。',
      links: searchLinks('水戸市', 'わら納豆'),
    },
  },
  {
    id: 'nakaminato-yakisoba',
    level: 3,
    name: '那珂湊焼きそば',
    shortName: '那珂湊焼きそば',
    image: '/assets/foods/ibaraki/03_nakaminato_yakisoba.webp',
    radius: 26,
    mass: 1.7,
    score: 8,
    description: '手間をかけてコシを出し、もっちりした食感に仕上げた「手延べせいろ蒸し」の麺を使う焼きそば。50年以上前から地元で愛されてきた、漁師町生まれのご当地焼きそばとされる。',
    source:
      'https://www.ibarakiguide.jp/gourmet/page001861.html',
    icon: { label: '焼きそば', shape: 'noodle-bowl', baseColor: '#8a5a2b', accentColor: '#3f8f3a' },
    reward: {
      unlockScore: 1000,
      area: 'ひたちなか市（那珂湊）',
      headline: 'せいろで蒸した手延べの麺',
      tip: '「那珂湊焼きそば大学院」の応援店で出されている。店ごとにさまざまなアレンジをしているので、食べ歩きや食べ比べができる。',
      links: searchLinks('ひたちなか市', '那珂湊焼きそば'),
      official: [{ label: '観光いばらき「いばらきの名物料理」', url: 'https://www.ibarakiguide.jp/gourmet/famous_food/ibarakifood.html' }],
    },
  },
  {
    id: 'stamina-ramen',
    level: 4,
    name: 'スタミナラーメン',
    shortName: 'スタミナラーメン',
    image: '/assets/foods/ibaraki/04_stamina_ramen.webp',
    radius: 30,
    mass: 2.2,
    score: 16,
    description: '昭和50年代に生まれた茨城のご当地ラーメン。冷水でしめたモチモチの太麺に、とろみのある熱々の甘辛い醤油あんをたっぷりのせる。あんにはキャベツを中心に、レバーやかぼちゃなどが入る。',
    source:
      'https://www.ibarakiguide.jp/gourmet/ramen.html',
    icon: { label: 'スタミナ', shape: 'noodle-bowl', baseColor: '#f0d890', accentColor: '#6a3a1e' },
    reward: {
      unlockScore: 1800,
      area: '水戸市・ひたちなか市ほか',
      headline: '冷たい太麺に熱々の甘辛あん',
      tip: 'あんに入る具は「カシラ」と呼ばれる。素揚げした緑黄色野菜も入り、コクのある一杯になる。',
      links: searchLinks('水戸市', 'スタミナラーメン'),
    },
  },
  {
    id: 'tsuke-kenchin-soba',
    level: 5,
    name: 'けんちんそば（つけけんちん）',
    shortName: 'つけけんちん',
    image: '/assets/foods/ibaraki/05_tsuke_kenchin_soba.webp',
    radius: 36,
    mass: 2.8,
    score: 32,
    description: '里芋、大根、ごぼう、こんにゃく、ねぎ、人参などを炒め、味噌と醤油、みりんで味をつけた温かいけんちん汁に、別に添えたざるそばをつけて食べる。太めのそばを使うのが特徴。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/tukekenchin_ibaraki.html',
    icon: { label: 'けんちん', shape: 'noodle-bowl', baseColor: '#7d6a58', accentColor: '#d9772b' },
    reward: {
      unlockScore: 3000,
      area: '茨城県全域（県北が本場）',
      headline: '温かい汁に冷たいそばをつける',
      tip: '江戸時代の後期にはすでに食べられていたとされる。農作物の収穫がほぼ済む11月半ばごろ、祭りのごちそうとしてよく作られた。',
      links: searchLinks('常陸太田市', 'つけけんちんそば'),
      official: [{ label: '観光いばらき「一度は食べたい茨城のご当地グルメ」（つけけんちんそば）', url: 'https://www.ibarakiguide.jp/gourmet/page001861.html' }],
    },
  },
  {
    id: 'gomoku-inari',
    level: 6,
    name: '笠間いなり寿司',
    shortName: '笠間いなり',
    image: '/assets/foods/ibaraki/06_gomoku_inari.webp',
    radius: 42,
    mass: 3.5,
    score: 64,
    description: '油揚げの中に、そばやくるみが入ったいなり寿司。笠間市は「笠間の新たな名物」と紹介している。市内には、茨城産の常陸秋そばを使うそば屋が軒を連ねる。',
    source:
      'https://www.city.kasama.lg.jp/page/page000864.html',
    icon: { label: 'いなり', shape: 'mochi', baseColor: '#c98a3c', accentColor: '#6b5a4a' },
    reward: {
      unlockScore: 4500,
      area: '笠間市',
      headline: 'そばを詰めたいなり寿司',
      tip: '笠間稲荷神社からキツネ、キツネから油揚げ、そしていなり寿司へとつながる名物。そば・くるみ・舞茸など、店ごとに変わり種のいなり寿司を出している。',
      links: searchLinks('笠間市', '笠間いなり寿司'),
      official: [{ label: '観光いばらき「一度は食べたい茨城のご当地グルメ」（笠間いなり寿司）', url: 'https://www.ibarakiguide.jp/gourmet/page001861.html' }],
    },
  },
  {
    id: 'ibaraki-shirasu-don',
    level: 7,
    name: 'しらす丼',
    shortName: 'しらす丼',
    image: '/assets/foods/ibaraki/07_ibaraki_shirasu_don.webp',
    radius: 46,
    mass: 4.4,
    score: 128,
    description: 'ご飯にしらすをのせた丼。しらすはおもにカタクチイワシやマイワシなどイワシ類の稚魚で、とれた時は半透明、ゆでると白くなることから「白子（しらす）」と呼ばれる。その日にとれたものなら生でも食べられる。',
    source:
      'https://www.ibaraki-shokusai.net/season/season6',
    icon: { label: 'しらす丼', shape: 'rice-bowl', baseColor: '#e9e4d6', accentColor: '#7aa86a' },
    reward: {
      unlockScore: 6500,
      area: '北茨城市・日立市・大洗町・鹿嶋市ほか県沿岸',
      headline: 'その日にとれたしらすを生で',
      tip: 'しらすは茨城の沿岸全域でとれ、北茨城市、日立市、大洗町、鹿嶋市で漁獲が多い。生しらすは昔は漁師だけのぜいたくだったが、近ごろは生で出す店も増えた。',
      links: searchLinks('大洗町', '生しらす丼'),
      official: [{ label: '観光いばらき「いばらきの定番グルメ！」（しらす丼）', url: 'https://www.ibarakiguide.jp/gourmet/ibaraki_major_food.html' }],
    },
  },
  {
    id: 'okukuji-shamo-oyakodon',
    level: 8,
    name: '奥久慈しゃも親子丼',
    shortName: 'しゃも親子丼',
    image: '/assets/foods/ibaraki/08_okukuji_shamo_oyakodon.webp',
    radius: 51,
    mass: 5.4,
    score: 256,
    description: '奥久慈しゃもを使った親子丼。奥久慈しゃもは、肉も卵も味のよいしゃもを茨城県養鶏試験場が改良して生まれた地鶏で、身が締まり脂肪分が少なく、歯ごたえがある。大子町ではそばやカレーとともに親子丼で味わえる。',
    source:
      'https://www.daigo-kanko.jp/tokusan/shamo.html',
    icon: { label: 'しゃも丼', shape: 'rice-bowl', baseColor: '#e8b23a', accentColor: '#7a3f1f' },
    reward: {
      unlockScore: 9000,
      area: '大子町',
      headline: '身の締まった地鶏の親子丼',
      tip: '一般的なブロイラーが約50日で育つのに対し、奥久慈しゃもは雄で125日、雌で155日かけて育てる。全国特殊鶏味の品評会で第1位を取ったこともある。',
      links: searchLinks('大子町', '奥久慈しゃも 親子丼'),
      official: [{ label: '茨城県「茨城をたべよう」奥久慈しゃも', url: 'https://www.ibaraki-shokusai.net/brand/chicken/' }],
    },
  },
  {
    id: 'kasumigaura-wakasagi-tempura',
    level: 9,
    name: '霞ヶ浦のわかさぎの天ぷら',
    shortName: 'わかさぎ天ぷら',
    image: '/assets/foods/ibaraki/09_kasumigaura_wakasagi_tempura.webp',
    radius: 57,
    mass: 6.6,
    score: 512,
    description: '霞ヶ浦北浦はエサのプランクトンが豊富で、そこでとれるワカサギは成長が早く脂がたっぷり。大きくても骨がやわらかく丸ごと食べられる。地元では天ぷらや唐揚げ、佃煮など、大きさに合わせて料理する。',
    source:
      'https://www.ibaraki-shokusai.net/brand/wakasagi/',
    icon: { label: 'わかさぎ', shape: 'fish', baseColor: '#e7c27a', accentColor: '#8a9aa6' },
    reward: {
      unlockScore: 12000,
      area: '土浦市・かすみがうら市・行方市ほか霞ヶ浦北浦沿岸',
      headline: '骨までやわらかい湖の小魚',
      tip: '霞ヶ浦のワカサギ漁は、昭和40年代はじめごろまで、真っ白な大きな帆を張った帆びき船で行われていた。主な漁期は7月下旬から12月末まで。',
      links: searchLinks('土浦市', 'わかさぎ 天ぷら'),
    },
  },
  {
    id: 'ushikunuma-unaju',
    level: 10,
    name: '牛久沼のうな重',
    shortName: '牛久沼のうな重',
    image: '/assets/foods/ibaraki/10_ushikunuma_unaju.webp',
    radius: 63,
    mass: 8,
    score: 1024,
    description: 'うなぎを下焼きし、ていねいに蒸してから、たれにくぐらせながらじっくり焼く。龍ケ崎市の牛久沼のほとりには何十年も続くうなぎの老舗が並び、「うなぎ街道」と呼ばれる。市はこの地を「うな丼発祥の地」としている。',
    source:
      'https://www.city.ryugasaki.ibaraki.jp/kanko/kankokyokai/more-ryugasaki/kanko_yomimono/2013081500954.html',
    icon: { label: 'うな重', shape: 'rice-bowl', baseColor: '#7a3f1f', accentColor: '#1c1c1c' },
    reward: {
      unlockScore: 16000,
      area: '龍ケ崎市（牛久沼）',
      headline: '牛久沼のほとりのうなぎ街道',
      tip: '江戸時代後期、大久保今助が牛久沼の渡し船を待つ間に蒲焼きとどんぶり飯を頼み、飯に蒲焼きの皿をかぶせて船に乗った。対岸で食べるとことのほかうまかった、という話が伝わる。',
      links: searchLinks('龍ケ崎市', '牛久沼 うなぎ'),
    },
  },
  {
    id: 'hitachigyu-steak',
    level: 11,
    name: '常陸牛ステーキ',
    shortName: '常陸牛ステーキ',
    image: '/assets/foods/ibaraki/11_hitachigyu_steak.webp',
    radius: 70,
    mass: 9.6,
    score: 2048,
    description: '茨城県の銘柄牛、常陸牛のステーキ。常陸牛は、指定生産者が30か月にわたって育てた黒毛和牛のうち、食肉取引規格A・Bの4と5等級に格付けされたものだけの銘柄。サーロインやヒレは、赤身にほどよく脂の入ったやわらかい霜降り。',
    source:
      'https://www.ibaraki-shokusai.net/brand/beef/',
    icon: { label: '常陸牛', shape: 'feast', baseColor: '#8f2f2a', accentColor: '#e8d2b0' },
    reward: {
      unlockScore: 22000,
      area: '茨城県全域',
      headline: '4・5等級だけの黒毛和牛',
      tip: '茨城の肉用牛のルーツは、天保3年に徳川斉昭が今の水戸市内に桜野牧を設け、牛を飼ったことにさかのぼる。「常陸牛」の名は昭和51年に生まれた。',
      links: searchLinks('水戸市', '常陸牛 ステーキ'),
      official: [{ label: '観光いばらき「いばらきの定番グルメ！」（常陸牛）', url: 'https://www.ibarakiguide.jp/gourmet/ibaraki_major_food.html' }],
    },
  },
  {
    id: 'anko-nabe',
    level: 12,
    name: 'あんこう鍋',
    shortName: 'あんこう鍋',
    image: '/assets/foods/ibaraki/12_anko_nabe.webp',
    radius: 78,
    mass: 11.4,
    score: 4096,
    description: 'あんこうの7つ道具といわれる身・ヒレ・皮・肝臓・エラ・胃袋・卵巣を使う鍋。肝を溶かした濃厚な漁師料理を「どぶ汁」といい、水を使わず、大根などの野菜と味噌だけで肝を溶かして作る。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/ankounotomozu_ibaraki.html',
    icon: { label: 'あんこう鍋', shape: 'pot', baseColor: '#c2a86a', accentColor: '#8a5a2a' },
    reward: {
      unlockScore: 30000,
      area: '北茨城市／大洗町／ひたちなか市',
      headline: '西のフグ、東のアンコウ',
      tip: '下ごしらえには「つるし切り」という特殊な捌き方を使う。肝は江戸時代の五大珍味「三鳥二魚」に数えられ、水戸藩からの献上品でもあった。12月から2月が最も肝が肥えて味がよい。',
      links: searchLinks('北茨城市', 'あんこう鍋'),
    },
  },
];

/** 得点は 2 ^ level を基準にしている（GAME_TUNING.score で方式変更可能）。 */
export const ibarakiFoodStage: StageDefinition = {
  regionId: 'ibaraki',
  regionName: '茨城県',
  mode: 'food',
  title: '茨城めしマージ',
  theme: {
    primaryColor: '#6e3a72',
    secondaryColor: '#ece3d6',
    accentColor: '#3f7fb5',
    backgroundColor: '#f9f4ea',
  },
  /** 背景ツアー「茨城めぐり」。軸は季節。4月末のネモフィラから始めて、3月の筑波山で終わる。 */
  scenery: {
    tour: ibarakiSceneryTour,
  },
  items: attachHulls(FOOD_ITEMS, IBARAKI_FOOD_HULLS),
};
